<template>
  <h1>{{ tn("h1Heading") }}</h1>
  <div class="border-bottom mb">
    <p class="mb"><svg-icon-component icon="logo-allstate" /></p>
    <p>{{ tn("summaryText") }}</p>
    <p v-if="FLAGS.ENABLE_ALLSTATE_FAMILY === 'true'">{{ tn("summaryText2") }}</p>
  </div>
  <div v-if="subscriptionsToDisplay.length > 1">
    <i18n-t keypath="AllstateUpgradeComponent.chooseKeycodeLine1" tag="p" scope="global">
      <template #onlyOne>
        <strong>{{ tn("onlyOne") }}</strong>
      </template>
    </i18n-t>
    <p class="border-bottom mb-0">{{ tn("chooseKeycodeLine2") }}:</p>
    <ul class="form max-char-65 constrain-90 mb">
      <li
        v-for="sub in subscriptionsToDisplay"
        :id="`keycodeLi_${sub.keyCode}`"
        :key="sub.keyCode"
        class="radio border-bottom mb-0 pb-quarter pt-quarter"
      >
        <input
          :id="`keycodeRadio_${sub.keyCode}`"
          v-model="selectedKeycode"
          type="radio"
          name="keycodeSelector"
          :value="sub.keyCode"
        />
        <label :for="`keycodeRadio_${sub.keyCode}`" class="mr">{{ getSubName(sub) }}</label>
        {{
          tn(sub.expirationDays <= 0 ? "expired" : "expires") +
          formatDateString(sub.expirationDate === undefined ? "" : sub.expirationDate)
        }}
      </li>
    </ul>
  </div>
  <div class="mb-double">
    <button id="btn-buyAllstate" class="btn-primary" :disabled="!selectedKeycode" @click="buyAllstate()">
      {{ tn("btnDetailsAndPricing") }}
    </button>
  </div>
  <h2 v-if="FLAGS.ENABLE_ALLSTATE_FAMILY !== 'true'" class="mb-half">{{ tn("featureComparison") }}</h2>
  <table v-if="displayFeatures() && FLAGS.ENABLE_ALLSTATE_FAMILY !== 'true'" class="feature-compare-table pre-family">
    <tr>
      <th>&nbsp;</th>
      <th>
        {{ t("Brand.WSAP") }}
        <br />
        <small>{{ tn("premiumOption") }}</small>
      </th>
      <th>
        {{ tn("allstateIdProtectionAddOn") }}
        <br />
        <small>{{ tn("addOnOption") }}</small>
      </th>
    </tr>
    <tr v-for="f in features" :id="`feature_${f.id}`" :key="f.id" class="success">
      <td class="feature">
        <span v-if="f.subFeatures.length > 0" class="btn-link link-color pl-0" @click="toggleDisplayItems(f.id)">
          <span v-show="true" :id="`featureChevron_${f.id}_left`" class="mr-half">
            <svg-icon-component icon="chevron-right" class="icon-xs" />
          </span>
          <span v-show="false" :id="`featureChevron_${f.id}_down`" class="mr-half">
            <svg-icon-component icon="chevron-down" class="icon-xs" />
          </span>
          {{ f.feature }}
        </span>
        <span v-else>
          {{ f.feature }}
        </span>
      </td>
      <td class="text-center">
        <span v-if="f.webrootPremium === 'yes'">
          <svg-icon-component icon="circle-check" class="icon-sm" />
        </span>
        <span v-else>
          {{ f.webrootPremium }}
        </span>
      </td>
      <td class="text-center">
        <span v-if="f.addOn === 'yes'">
          <svg-icon-component icon="circle-check" class="icon-sm" />
        </span>
        <span v-else>
          {{ f.addOn }}
        </span>
      </td>
    </tr>
  </table>

  <div v-if="FLAGS.ENABLE_ALLSTATE_FAMILY === 'true'">
    <h2 class="mb-half">{{ tn("featuresComparison") }}</h2>
    <table v-if="displayFeatures()" class="feature-compare-table">
      <thead>
        <tr>
          <th class="no-borders-th-td"></th>
          <th colspan="3">
            <span class="block">{{ t("Common.option1") }}</span>
            <small>{{ tn("option1description") }}</small>
          </th>
          <th colspan="2">
            <span class="block">{{ t("Common.option2") }}</span>
            <small>{{ tn("option2description") }}</small>
          </th>
        </tr>
        <tr>
          <td class="no-borders-th-td"></td>
          <th colspan="3">{{ tn("allstateAddOn") }}</th>
          <th colspan="2">{{ t("Brand.WSAP") }}</th>
        </tr>
        <tr class="mobile-upright">
          <td class="no-borders-th-td"></td>
          <td class="constrain-10">
            <span>{{ t("Common.basic") }}</span>
          </td>
          <td class="constrain-10">
            <span>{{ t("Common.premium") }}</span>
          </td>
          <td class="constrain-10">
            <span>{{ t("Common.family") }}</span>
          </td>
          <td class="constrain-10">
            <span>{{ t("Common.individual") }}</span>
          </td>
          <td class="constrain-10">
            <span>{{ t("Common.family") }}</span>
          </td>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{{ tn("devicesProtected") }}</td>
          <td colspan="3" class="italic-text">{{ tn("devicesProtectedVary") }}</td>
          <td>5</td>
          <td>10</td>
        </tr>
        <tr>
          <td>{{ tn("identitiesProtected") }}</td>
          <td>1</td>
          <td>1</td>
          <td>10</td>
          <td>1</td>
          <td>10</td>
        </tr>
        <tr>
          <td>
            {{ tn("financialMonitor") }}
            <div class="has-tooltip">
              <button type="button" class="btn-link">
                <svg-icon-component icon="circle-information" class="icon-xs" />
              </button>
              <div class="tooltip-area">
                <p class="text-sm">
                  <strong>{{ tn("financialMonitor") }}</strong>
                </p>
                <ul class="disc">
                  <li>{{ tn("creditAndDebitCards") }}</li>
                  <li>{{ tn("bankAccountTransactions") }}</li>
                  <li>{{ tn("investmentAccounts") }}</li>
                  <li>{{ tn("accountTakeoverMonitoring") }}</li>
                </ul>
              </div>
            </div>
          </td>
          <td><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
        </tr>
        <tr>
          <td>
            {{ tn("darkWebMonitor") }}
            <div class="has-tooltip">
              <button type="button" class="btn-link">
                <svg-icon-component icon="circle-information" class="icon-xs" />
              </button>
              <div class="tooltip-area">
                <p class="text-sm">
                  <strong>{{ tn("darkWebMonitor") }}</strong>
                </p>
                <ul class="disc">
                  <li>{{ tn("humanSourcedIntelligence") }}</li>
                  <li>{{ tn("ssnMonitoring") }}</li>
                  <li>{{ tn("idMonitoring") }}</li>
                  <li>{{ tn("creditDebitScan") }}</li>
                  <li>{{ tn("breachNotifications") }}</li>
                </ul>
              </div>
            </div>
          </td>
          <td><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
        </tr>
        <tr>
          <td>
            {{ tn("identityMonitor") }}
            <div class="has-tooltip">
              <button type="button" class="btn-link">
                <svg-icon-component icon="circle-information" class="icon-xs" />
              </button>
              <div class="tooltip-area">
                <p class="text-sm">
                  <strong>{{ tn("identityMonitor") }}</strong>
                </p>
                <ul class="disc">
                  <li>{{ tn("healthStatus") }}</li>
                  <li>{{ tn("emergingThreatAndScamAlerts") }}</li>
                </ul>
              </div>
            </div>
          </td>
          <td><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
        </tr>
        <tr>
          <td>
            {{ tn("restoration") }}
            <div class="has-tooltip">
              <button type="button" class="btn-link">
                <svg-icon-component icon="circle-information" class="icon-xs" />
              </button>
              <div class="tooltip-area">
                <p class="text-sm">
                  <strong>{{ tn("restoration") }}</strong>
                </p>
                <ul class="disc">
                  <li>{{ tn("customerCare") }}</li>
                  <li>{{ tn("remediationSupport") }}</li>
                </ul>
              </div>
            </div>
          </td>
          <td><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
        </tr>
        <tr>
          <td>
            {{ tn("lostWallet") }}
          </td>
          <td><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
        </tr>
        <tr>
          <td>
            {{ tn("expenseReimbursement") }}
          </td>
          <td>$25K</td>
          <td colspan="2">$500K</td>
          <td colspan="2">$500K</td>
        </tr>
        <tr>
          <td>
            {{ tn("stolenFunds") }}
          </td>
          <td></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
        </tr>
        <tr>
          <td>
            {{ tn("creditMonitor") }}
            <div class="has-tooltip">
              <button type="button" class="btn-link">
                <svg-icon-component icon="circle-information" class="icon-xs" />
              </button>
              <div class="tooltip-area">
                <p class="text-sm">
                  <strong>{{ tn("creditMonitor") }}</strong>
                </p>
                <ul class="disc">
                  <li>{{ tn("rapidAlertDetection") }}</li>
                  <li>{{ tn("autoOnCapability") }}</li>
                  <li>{{ tn("transUnion") }}</li>
                </ul>
              </div>
            </div>
          </td>
          <td></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
          <td colspan="2"><svg-icon-component icon="circle-check" class="icon-sm success" /></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
<script lang="ts">
import { defineComponent, onBeforeMount, ref, PropType } from "vue";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";
import { formatDateString } from "@/common/dateFormat";
import { getProductDisplayNameBySku } from "@/common/productsBySku";
import { ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { IProductDisplayRequest } from "@/common/productsBySku";
import { registerRefreshFunction } from "@/components/shared/LanguageSelector/LanguageSelector";
import { listFeaturesForComparison } from "./features";
import { KeyCodeSeller } from "@/globalEnums";
import { unifiedApi } from "@/common";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import { FLAGS } from "@/define";
import { getUrl } from "@/common/getUrl";

const componentName = "AllstateUpgradeComponent";

export default defineComponent({
  name: componentName,
  components: {
    SvgIconComponent,
  },
  props: {
    subscriptions: {
      type: Object as PropType<ISubscription[]>,
      required: false,
      default: null,
    },
  },
  setup(props) {
    logEvent("created", componentName);

    const selectedKeycode = ref<string>();
    const subscriptionsToDisplay = ref<ISubscription[]>([]);
    const features = ref(listFeaturesForComparison());

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${componentName}.${v}`, params);
    }

    async function buyAllstate() {
      try {
        if (selectedKeycode.value) {
          var url = (await unifiedApi.getWebrootRenewUrl(selectedKeycode.value)).data.redirectUrl;
          if (url) {
            const key = url.split("=")[1];
            // This URL will be only used here, so no need to add to env files.
            window.open(getUrl("ALLSTATE_UPGRADE_CART").replace("{key}", key), "_blank");
          }
        }
      } catch (error) {
        handleApiError(error as AxiosError, true);
      }
    }

    function insertAfter(referenceNode, newNode) {
      referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
    }

    function toggleDisplayItems(id: number) {
      const element = document.getElementById(`subFeatures_${id}`);
      if (element) {
        hideItems(id);
      } else {
        displayItems(id);
      }
    }

    function displayItems(id: number) {
      const items = features.value.find(f => f.id === id)?.subFeatures;
      if (items) {
        const parent = document.getElementById(`feature_${id}`);
        const child = document.createElement("tr");
        child.id = `subFeatures_${id}`;
        let listItems = "";
        for (const item of items) {
          listItems += `<li>${item}</li>`;
        }
        child.innerHTML = `<td colspan="3" class="relative-indent"><ul class="disc">${listItems}</ul></td>`;
        insertAfter(parent, child);
        toggleChevron(id);
      }
    }

    function hideItems(id: number) {
      const child = document.getElementById(`subFeatures_${id}`);
      if (child) {
        child.remove();
        toggleChevron(id);
      }
    }

    function toggleChevron(id: number) {
      const chevronLeft = document.getElementById(`featureChevron_${id}_left`);
      const chevronDown = document.getElementById(`featureChevron_${id}_down`);
      if (chevronLeft?.style && chevronDown?.style) {
        chevronLeft.style.display = chevronLeft.style.display === "" ? "none" : "";
        chevronDown.style.display = chevronDown.style.display === "" ? "none" : "";
      }
    }

    onBeforeMount(async () => {
      logEvent("mounting", componentName);
      subscriptionsToDisplay.value = props.subscriptions.filter(
        s => s.keyCodeSellerType === KeyCodeSeller.Webroot && s.expirationDays > 0
      );
      if (subscriptionsToDisplay.value.length === 1) {
        selectedKeycode.value = subscriptionsToDisplay.value[0].keyCode;
      }
    });

    function getSubName(sub: ISubscription) {
      const request: IProductDisplayRequest = {
        sku: sub.sku,
        webrootLicenseKey: sub.keyCode,
        isTrial: sub.isTrial,
      };
      return t(getProductDisplayNameBySku(request)).toString();
    }

    function displayFeatures() {
      return features.value.length > 0;
    }

    function refreshCachedString() {
      // Hide sub features before refreshing;
      for (const feature of features.value) {
        hideItems(feature.id);
      }
      features.value = listFeaturesForComparison();
    }

    registerRefreshFunction(refreshCachedString);

    //Expose to template
    return {
      t,
      tn,
      buyAllstate,
      features,
      displayFeatures,
      toggleDisplayItems,
      subscriptionsToDisplay,
      formatDateString,
      selectedKeycode,
      getSubName,
      FLAGS,
    };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";
</style>
